import { useMutation, useQuery } from '@apollo/client'
import { Delete as DeleteIcon } from '@mui/icons-material'
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import FileUploadButton from 'candidate/components/FileUploadButton'
import config from 'config'
import isEqual from 'lodash.isequal'
import { createRef, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    AddCompany,
    DeleteCompany,
    GetFundSizeBucketOptions,
    GetIndustryOptions,
    GetSectorOptions,
    UpdateCompany,
} from 'recruiter/graphql/companies.gql'
import { recruiterRouteConfig } from 'recruiter/routes'
import { CompanyStatus } from 'recruiter/types/CompanyStatus'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError, getPxAsRem } from 'shared/utils'
import { DialogButtonContainer, StyledDialogContent } from './styled'

import type { GetCompanyQuery } from '__generated__/graphql'
import type { FC } from 'react'
import DrawerComponent from 'shared/components/DrawerComponent'
import OfficeLocationsAutocomplete from 'shared/components/OfficeLocationsAutocomplete/OfficeLocationsAutocomplete'
import OfficeLocationsAutocompleteMultiple from 'shared/components/OfficeLocationsAutocomplete/OfficeLocationsAutocompleteMultiple'

interface EditProfileProps {
    open: boolean
    onClose: () => void
    data?: GetCompanyQuery
    refetch: () => void
}

const bucketOptions = [
    { value: '', label: 'None' },
    { value: 'genpop', label: 'Gen Pop' },
    { value: 'star', label: 'Star' },
    { value: 'hold', label: 'Hold' },
    { value: 'realestate', label: 'Real Estate' },
    { value: 'overflow', label: 'Overflow' },
]

const EditProfile: FC<EditProfileProps> = ({
    open,
    onClose,
    data,
    refetch,
}) => {
    const isEditMode = Boolean(data)
    const toast = useToast()
    const navigate = useNavigate()
    const [formValues, setFormValues] = useState({
        company: data?.company.value ?? '',
        bucket: data?.company.bucket ?? '',
        status: data?.company.status?.status ?? '',
        primaryIndustry:
            data?.company.industries?.find((industry) => industry.isPrimary) ??
            null,
        topRatedIndustries:
            data?.company.industries?.filter(
                (industry: any) => industry.topTier
            ) ?? [],
        otherIndustries:
            data?.company.industries?.filter(
                (industry: any) => !industry.topTier
            ) ?? [],
        topRatedSectors:
            data?.company.sectors?.filter((sector: any) => sector.topTier) ??
            [],
        otherSectors:
            data?.company.sectors?.filter((sector: any) => !sector.topTier) ??
            [],
        websiteUrl: data?.company.url ?? '',
        hqLocation: data?.company.headquarters?.officeLocation,
        otherLocations:
            data?.company.companyOffices
                ?.filter((companyOffice: any) => !companyOffice.isHeadquarters)
                .map((co) => co.officeLocation)
                .filter((val) => val !== undefined && val !== null) ?? [],
        nonPoachDate: data?.company.nonPoachDate ?? '',
        fundSizeBuckets: data?.company.fundSizeBuckets ?? [],
        inCandidatePortal: data?.company.inCandidatePortal ?? true,
    })

    const inputRef = createRef<HTMLInputElement>()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [image, setImage] = useState('')
    const [imageLoading, setImageLoading] = useState(false)
    const [imageFile, setImageFile] = useState<File>()
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
    const [errorValues, setErrorValues] = useState({
        company: '',
        status: '',
        primaryIndustry: '',
        websiteUrl: '',
        hqLocation: '',
        fundSize: '',
        fundraiseYear: '',
        nonPoachDate: '',
    })

    const { data: industryData } = useQuery(GetIndustryOptions, {
        onError: (error) => {
            console.log('error fetching industries', error)
            toast({
                title: 'Something went wrong',
                severity: 'error',
                message: 'Please try again',
            })
        },
    })
    const industries = industryData?.industries ?? []

    const { data: sectorData } = useQuery(GetSectorOptions, {
        onError: (error) => {
            console.log('error fetching sectors', error)
            toast({
                title: 'Something went wrong',
                severity: 'error',
                message: 'Please try again',
            })
        },
    })
    const sectors = sectorData?.sectors ?? []

    const { data: fundSizeBucketData } = useQuery(GetFundSizeBucketOptions)
    const fundSizeBucketOptions = fundSizeBucketData?.fundSizeBuckets ?? []

    const [profileImage, setProfileImage] = useState<string | undefined>(
        data?.company.profileImageKey ?? undefined
    )

    const resetForm = useCallback(() => {
        setFormValues({
            company: data?.company.value ?? '',
            bucket: data?.company.bucket ?? '',
            status: data?.company.status?.status ?? '',
            primaryIndustry:
                data?.company.industries?.find(
                    (industry) => industry.isPrimary
                ) ?? null,
            topRatedIndustries:
                data?.company.industries?.filter(
                    (industry: any) => industry.topTier
                ) ?? [],
            otherIndustries:
                data?.company.industries?.filter(
                    (industry: any) => !industry.topTier
                ) ?? [],
            topRatedSectors:
                data?.company.sectors?.filter(
                    (sector: any) => sector.topTier
                ) ?? [],
            otherSectors:
                data?.company.sectors?.filter(
                    (sector: any) => !sector.topTier
                ) ?? [],
            websiteUrl: data?.company.url ?? '',
            hqLocation: data?.company.headquarters?.officeLocation,
            otherLocations:
                data?.company.companyOffices
                    ?.filter(
                        (companyOffice: any) => !companyOffice.isHeadquarters
                    )
                    .map((co) => co.officeLocation)
                    .filter((val) => val !== undefined && val !== null) ?? [],
            nonPoachDate: data?.company.nonPoachDate ?? '',
            fundSizeBuckets: data?.company.fundSizeBuckets ?? [],
            inCandidatePortal: data?.company.inCandidatePortal ?? true,
        })
        setErrorValues({
            company: '',
            status: '',
            primaryIndustry: '',
            websiteUrl: '',
            hqLocation: '',
            fundSize: '',
            fundraiseYear: '',
            nonPoachDate: '',
        })
    }, [data])

    // profile pic handlers
    const handleSelectFile = (e) => {
        const file = e.target.files[0]
        if (file) {
            setImageFile(file)
            setImage(URL.createObjectURL(file))
            setDialogOpen(true)
        }
    }

    const handleUpload = () => {
        if (imageFile) {
            if (!isEditMode) {
                setProfileImage(URL.createObjectURL(imageFile))
                closeDialog()
            } else {
                saveImage(imageFile, data?.company.id || '')
            }
        }
    }

    const closeDialog = () => {
        setDialogOpen(false)
        setImageLoading(false)
        if (inputRef.current) inputRef.current.value = ''
    }

    async function saveImage(image: File, id: string): Promise<void> {
        const formData = new FormData()
        formData.append('picture', image)
        formData.append('companyId', id || '')
        try {
            const response = await fetch(`${config.api.uri}/profilePicture`, {
                method: 'POST',
                body: formData,
                headers: {
                    accept: 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            })
            const data = await response.json()
            if (response.status === 200 && data?.signedUrl) {
                setProfileImage(data.signedUrl)
                refetch()
                if (isEditMode === false) {
                    toast({
                        title: 'Success',
                        message: 'Profile image updated',
                        severity: 'success',
                    })
                }
            } else {
                console.error('image upload error: ', data)
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
            }
        } catch (error) {
            console.error('image upload error: ', error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
        } finally {
            closeDialog()
        }
    }

    const handleCancel = () => {
        resetForm()
        onClose()
    }

    const handleDelete = (field, index) => {
        setFormValues((prev) => {
            const updatedArray = [...prev[field]]
            updatedArray.splice(index, 1)
            return {
                ...prev,
                [field]: updatedArray,
            }
        })
    }

    const handleOpenDeleteDialog = () => {
        setOpenDeleteDialog(true)
    }

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false)
    }

    const [addCompany] = useMutation(AddCompany, {
        onCompleted: (data) => {
            if (data.addCompany) {
                if (imageFile) {
                    saveImage(imageFile, data.addCompany.id)
                }
                toast({
                    title: 'Success',
                    severity: 'success',
                    message: 'Company profile added successfully',
                })
                setFormValues({
                    company: '',
                    bucket: '',
                    status: '',
                    primaryIndustry: null,
                    topRatedIndustries: [],
                    otherIndustries: [],
                    topRatedSectors: [],
                    otherSectors: [],
                    websiteUrl: '',
                    hqLocation: null,
                    otherLocations: [],
                    nonPoachDate: '',
                    fundSizeBuckets: [],
                    inCandidatePortal: true,
                })
                refetch()
                onClose()
            } else {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: 'Failed to add company profile',
                })
            }
        },
        onError: (error) => {
            console.log('error adding company', error)
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to add company profile'
                ),
            })
        },
    })

    const [updateCompany] = useMutation(UpdateCompany, {
        onCompleted: (data) => {
            if (data.updateCompany.success) {
                toast({
                    title: 'Success',
                    severity: 'success',
                    message: 'Company profile updated successfully',
                })
                refetch()
                onClose()
            } else {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message:
                        data.updateCompany?.message ??
                        'Failed to update company profile',
                })
            }
        },
        onError: (error) => {
            console.log('error updating company', error)
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to update company profile'
                ),
            })
        },
    })

    const [deleteCompany] = useMutation(DeleteCompany, {
        onCompleted: (data) => {
            setOpenDeleteDialog(false)
            if (data.deleteCompany.success) {
                navigate(recruiterRouteConfig.companies.path, { replace: true })
                toast({
                    title: 'Success',
                    severity: 'success',
                    message:
                        data.deleteCompany.message ??
                        'Company deleted successfully',
                })
                refetch()
                onClose()
            } else {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message:
                        data.deleteCompany.message ??
                        'Failed to delete company',
                })
            }
        },
        onError: (error) => {
            console.log('error deleting company', error)
            setOpenDeleteDialog(false)
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(error, 'Failed to delete company'),
            })
        },
    })

    const unsavedChangesExist = useMemo(() => {
        const apiData = {
            company: data?.company.value,
            bucket: data?.company.bucket ?? '',
            status: data?.company.status?.status,
            primaryIndustry: data?.company.industries?.find(
                (industry: any) => industry.isPrimary
            ),
            topRatedIndustries: data?.company.industries?.filter(
                (industry: any) => industry.topTier
            ),
            otherIndustries: data?.company.industries?.filter(
                (industry: any) => !industry.topTier
            ),
            topRatedSectors:
                data?.company.sectors?.filter(
                    (sector: any) => sector.topTier
                ) ?? [],
            otherSectors:
                data?.company.sectors?.filter(
                    (sector: any) => !sector.topTier
                ) ?? [],
            websiteUrl: data?.company.url,
            hqLocation: data?.company.headquarters,
            otherLocations: data?.company.companyOffices,
            nonPoachDate: data?.company.nonPoachDate,
            fundSizeBuckets: data?.company.fundSizeBuckets ?? [],
            inCandidatePortal: data?.company.inCandidatePortal ?? true,
        }
        if (!isEqual(formValues, apiData)) return true
        return false
    }, [formValues, data])

    const handleSave = async () => {
        const input = {
            companyId: data?.company.id,
            name: formValues.company,
            hqOfficeId: formValues.hqLocation?.id,
            officeLocationIds: formValues.otherLocations.map(
                (item) => item?.id ?? ''
            ),
            otherIndustryIds: formValues.otherIndustries.map(
                (item) => item.industry.id
            ),
            otherSectorIds: formValues.otherSectors.map(
                (item) => item.sector.id
            ),
            primaryIndustryId: formValues.primaryIndustry?.industry.id,
            status: formValues.status,
            topTierIndustryIds: formValues.topRatedIndustries.map(
                (item) => item.industry.id
            ),
            topTierSectorIds: formValues.topRatedSectors.map(
                (item) => item.sector.id
            ),
            url: formValues.websiteUrl,
            bucket: formValues.bucket || null,
            inCandidatePortal: formValues.inCandidatePortal,
            fundSizeBucketIds: formValues.fundSizeBuckets.map((b) => b.id),
            ...(formValues.nonPoachDate && {
                nonPoachDate: formValues.nonPoachDate,
            }),
        }

        if (isEditMode) {
            await updateCompany({
                variables: {
                    input: { ...input, companyId: data?.company.id },
                },
            })
        } else {
            await addCompany({
                variables: {
                    input: input,
                },
            })
        }
    }

    const handleDeleteCompany = async () => {
        await deleteCompany({
            variables: {
                input: {
                    companyId: data?.company.id ?? '',
                },
            },
        })
    }

    const filteredOtherIndustriesOptions = industries.filter(
        (industry: any) => {
            return !formValues.topRatedIndustries?.some(
                (topIndustry: any) =>
                    topIndustry.industry?.value === industry.value
            )
        }
    )

    const filteredOtherSectorsOptions = sectors.filter((sector: any) => {
        return !formValues.topRatedSectors?.some(
            (topSector: any) => topSector.sector?.value === sector.value
        )
    })

    return (
        <>
            <DrawerComponent
                open={open}
                onClose={handleCancel}
                hasCancelButton
                onCancel={handleCancel}
                headerTitle={isEditMode ? 'Edit Profile' : 'Add Company'}
                primaryActionText={isEditMode ? 'Update' : 'Save'}
                saveButtonDisabled={
                    !formValues.company ||
                    !unsavedChangesExist ||
                    Object.values(errorValues).some((error) => error !== '')
                }
                onPrimaryAction={handleSave}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: getPxAsRem(16),
                        margin: getPxAsRem(16, 0, 24),
                    }}
                >
                    <Avatar src={profileImage} />
                    <FileUploadButton
                        label="change"
                        ref={inputRef}
                        onChange={handleSelectFile}
                    />
                </Box>
                <Stack direction="column" gap={3}>
                    <TextField
                        fullWidth
                        label="Company"
                        value={formValues.company}
                        onChange={(e) => {
                            setFormValues((prev) => ({
                                ...prev,
                                company: e.target.value,
                            }))
                            if (!e.target.value || e.target.value === '') {
                                setErrorValues((prev) => ({
                                    ...prev,
                                    company: 'Company is required',
                                }))
                            } else {
                                setErrorValues((prev) => ({
                                    ...prev,
                                    company: '',
                                }))
                            }
                        }}
                        error={!!errorValues.company}
                        helperText={errorValues.company}
                    />
                    <Autocomplete
                        onChange={(event, newValue) => {
                            setFormValues((prev: any) => ({
                                ...prev,
                                status: newValue || '',
                            }))
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Status" />
                        )}
                        value={formValues.status}
                        options={Object.values(CompanyStatus)}
                    />
                    {formValues.status === CompanyStatus['Non-poach'] && (
                        <DatePicker
                            value={
                                formValues.nonPoachDate
                                    ? new Date(formValues.nonPoachDate)
                                    : undefined
                            }
                            onChange={(newValue) =>
                                setFormValues((prev) => ({
                                    ...prev,
                                    nonPoachDate: newValue?.toISOString() || '',
                                }))
                            }
                            label={'Non-Poach Date'}
                        />
                    )}
                    <Autocomplete
                        onChange={(_event, newValue) => {
                            setFormValues((prev) => ({
                                ...prev,
                                bucket: newValue?.value || '',
                            }))
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Bucket" />
                        )}
                        value={
                            bucketOptions.find(
                                (o) => o.value === formValues.bucket
                            ) ?? null
                        }
                        options={bucketOptions}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                        }
                    />
                    <Stack spacing={2}>
                        <Autocomplete
                            options={[
                                '',
                                ...industries.map((ind) => ind.value),
                            ]}
                            value={
                                formValues.primaryIndustry?.industry.value ?? ''
                            }
                            onChange={(event, newValue) => {
                                setFormValues((prev) => {
                                    const updatedPrimaryIndustry =
                                        industries.find(
                                            (industry) =>
                                                industry.value === newValue
                                        )

                                    if (!updatedPrimaryIndustry) {
                                        setErrorValues((prevErrorValues) => ({
                                            ...prevErrorValues,
                                            primaryIndustry:
                                                'Primary industry is required',
                                        }))

                                        return prev
                                    } else {
                                        setErrorValues((prevErrorValues) => ({
                                            ...prevErrorValues,
                                            primaryIndustry: '',
                                        }))
                                    }

                                    return {
                                        ...prev,
                                        primaryIndustry: {
                                            id: updatedPrimaryIndustry.id,
                                            industry: updatedPrimaryIndustry,
                                            isPrimary: true,
                                            topTier: false,
                                        },
                                    }
                                })
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Primary Industry"
                                    fullWidth
                                    variant="outlined"
                                    error={!!errorValues.primaryIndustry}
                                    helperText={errorValues.primaryIndustry}
                                />
                            )}
                        />
                    </Stack>
                    <Autocomplete
                        multiple
                        options={industries.map(
                            (industry: any) => industry.value
                        )}
                        value={
                            Array.isArray(formValues.topRatedIndustries)
                                ? formValues.topRatedIndustries.map(
                                      (industry: any) =>
                                          industry.industry?.value
                                  )
                                : []
                        }
                        onChange={(event, newValue) => {
                            setFormValues((prev: any) => {
                                const updatedTopRatedIndustries = newValue.map(
                                    (val) => {
                                        const existing: any = industries.find(
                                            (industry: any) =>
                                                industry.value === val
                                        )
                                        return existing
                                            ? {
                                                  industry: {
                                                      id: existing.id,
                                                      value: val,
                                                  },
                                              }
                                            : { industry: { value: val } }
                                    }
                                )
                                return {
                                    ...prev,
                                    topRatedIndustries:
                                        updatedTopRatedIndustries,
                                }
                            })
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Top Rated Industries"
                                fullWidth
                                variant="outlined"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({
                                    index,
                                })

                                return (
                                    <Chip
                                        label={option}
                                        key={key}
                                        {...tagProps}
                                        onDelete={() =>
                                            handleDelete(
                                                'topRatedIndustries',
                                                index
                                            )
                                        }
                                    />
                                )
                            })
                        }
                    />
                    <Autocomplete
                        multiple
                        options={filteredOtherIndustriesOptions.map(
                            (industry: any) => industry.value
                        )}
                        value={
                            Array.isArray(formValues.otherIndustries)
                                ? formValues.otherIndustries.map(
                                      (industry: any) =>
                                          industry.industry?.value
                                  )
                                : []
                        }
                        onChange={(event, newValue) => {
                            setFormValues((prev: any) => {
                                const updatedOtherIndustries = newValue.map(
                                    (val) => {
                                        const existing: any = industries.find(
                                            (industry: any) =>
                                                industry.value === val
                                        )
                                        return existing
                                            ? {
                                                  industry: {
                                                      id: existing.id,
                                                      value: val,
                                                  },
                                              }
                                            : { industry: { value: val } }
                                    }
                                )
                                return {
                                    ...prev,
                                    otherIndustries: updatedOtherIndustries,
                                }
                            })
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Other Industries"
                                fullWidth
                                variant="outlined"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({
                                    index,
                                })

                                return (
                                    <Chip
                                        label={option}
                                        key={key}
                                        {...tagProps}
                                        onDelete={() =>
                                            handleDelete(
                                                'otherIndustries',
                                                index
                                            )
                                        }
                                    />
                                )
                            })
                        }
                    />

                    <Autocomplete
                        multiple
                        options={sectors.map((sector: any) => sector.value)}
                        value={
                            Array.isArray(formValues.topRatedSectors)
                                ? formValues.topRatedSectors.map(
                                      (sector: any) => sector.sector?.value
                                  )
                                : []
                        }
                        onChange={(event, newValue) => {
                            setFormValues((prev: any) => {
                                const updatedTopRatedSectors = newValue.map(
                                    (val) => {
                                        const existing: any = sectors.find(
                                            (sector: any) =>
                                                sector.value === val
                                        )
                                        return existing
                                            ? {
                                                  sector: {
                                                      id: existing.id,
                                                      value: val,
                                                  },
                                              }
                                            : { sector: { value: val } }
                                    }
                                )
                                return {
                                    ...prev,
                                    topRatedSectors: updatedTopRatedSectors,
                                }
                            })
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Top Rated Sectors"
                                fullWidth
                                variant="outlined"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({
                                    index,
                                })

                                return (
                                    <Chip
                                        label={option}
                                        key={key}
                                        {...tagProps}
                                        onDelete={() =>
                                            handleDelete(
                                                'topRatedSectors',
                                                index
                                            )
                                        }
                                    />
                                )
                            })
                        }
                    />
                    <Autocomplete
                        multiple
                        options={filteredOtherSectorsOptions.map(
                            (industry: any) => industry.value
                        )}
                        value={
                            Array.isArray(formValues.otherSectors)
                                ? formValues.otherSectors.map(
                                      (sector: any) => sector.sector?.value
                                  )
                                : []
                        }
                        onChange={(event, newValue) => {
                            setFormValues((prev: any) => {
                                const updatedOtherSectors = newValue.map(
                                    (val) => {
                                        const existing: any = sectors.find(
                                            (sector: any) =>
                                                sector.value === val
                                        )
                                        return existing
                                            ? {
                                                  sector: {
                                                      id: existing.id,
                                                      value: val,
                                                  },
                                              }
                                            : { sector: { value: val } }
                                    }
                                )
                                return {
                                    ...prev,
                                    otherSectors: updatedOtherSectors,
                                }
                            })
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Other Sectors"
                                fullWidth
                                variant="outlined"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({
                                    index,
                                })

                                return (
                                    <Chip
                                        label={option}
                                        key={key}
                                        {...tagProps}
                                        onDelete={() =>
                                            handleDelete('otherSectors', index)
                                        }
                                    />
                                )
                            })
                        }
                    />
                    <Autocomplete
                        multiple
                        options={fundSizeBucketOptions}
                        getOptionLabel={(option) => option.value}
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        value={formValues.fundSizeBuckets}
                        onChange={(event, newValue) => {
                            setFormValues((prev) => ({
                                ...prev,
                                fundSizeBuckets: newValue,
                            }))
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Fund Size"
                                fullWidth
                                variant="outlined"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({
                                    index,
                                })
                                return (
                                    <Chip
                                        label={option.value}
                                        key={key}
                                        {...tagProps}
                                        onDelete={() =>
                                            handleDelete(
                                                'fundSizeBuckets',
                                                index
                                            )
                                        }
                                    />
                                )
                            })
                        }
                    />
                    <TextField
                        fullWidth
                        label="Website URL"
                        value={formValues.websiteUrl}
                        onChange={(e) => {
                            setFormValues((prev) => ({
                                ...prev,
                                websiteUrl: e.target.value,
                            }))
                            if (!e.target.value || e.target.value === '') {
                                setErrorValues((prev) => ({
                                    ...prev,
                                    websiteUrl: 'Website URL is required',
                                }))
                            } else {
                                setErrorValues((prev) => ({
                                    ...prev,
                                    websiteUrl: '',
                                }))
                            }
                        }}
                        variant="outlined"
                        error={!!errorValues.websiteUrl}
                        helperText={errorValues.websiteUrl}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formValues.inCandidatePortal}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        inCandidatePortal: e.target.checked,
                                    }))
                                }}
                            />
                        }
                        label="Show in Candidate Portal"
                    />

                    <OfficeLocationsAutocomplete
                        value={formValues.hqLocation}
                        onChange={(event, newValue) => {
                            setFormValues((prev: any) => {
                                return {
                                    ...prev,
                                    hqLocation: newValue,
                                }
                            })
                        }}
                        label="HQ Location"
                    />

                    <OfficeLocationsAutocompleteMultiple
                        value={formValues.otherLocations}
                        onChange={(event, newValue) => {
                            setFormValues((prev: any) => {
                                return {
                                    ...prev,
                                    otherLocations: newValue,
                                }
                            })
                        }}
                        label="Other Locations"
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({
                                    index,
                                })

                                return (
                                    <Chip
                                        label={option.value}
                                        key={key}
                                        {...tagProps}
                                        onDelete={() =>
                                            handleDelete(
                                                'otherLocations',
                                                index
                                            )
                                        }
                                    />
                                )
                            })
                        }
                    />

                    {isEditMode && (
                        <Box
                            display="flex"
                            justifyContent="flex-start"
                            alignItems="center"
                        >
                            <Button
                                startIcon={<DeleteIcon />}
                                variant="text"
                                color="error"
                                onClick={handleOpenDeleteDialog}
                            >
                                Delete Company
                            </Button>
                        </Box>
                    )}
                </Stack>
            </DrawerComponent>
            <Dialog
                open={openDeleteDialog}
                onClose={handleCloseDeleteDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {'Are you sure?'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        This company will be permanently deleted.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="text"
                        onClick={handleCloseDeleteDialog}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteCompany}
                        color="primary"
                        autoFocus
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Edit profile image dialog */}
            <Dialog
                open={dialogOpen}
                onClose={!imageLoading ? closeDialog : undefined}
            >
                {imageLoading ? (
                    <StyledDialogContent>
                        <CircularProgress />
                        <Typography variant="h6" component="h1">
                            Uploading new profile picture
                        </Typography>
                    </StyledDialogContent>
                ) : (
                    <StyledDialogContent>
                        <Avatar src={image} sx={{ width: 180, height: 180 }} />
                        <DialogButtonContainer>
                            <Button
                                variant="outlined"
                                onClick={closeDialog}
                                type="button"
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleUpload}>Upload Image</Button>
                        </DialogButtonContainer>
                    </StyledDialogContent>
                )}
            </Dialog>
        </>
    )
}

export default EditProfile
